/*
Which travel-cost calculation method a request actually uses.

Road-network costs (the 'driving' method) are a paid feature: the backend refuses
them to free and signed-out users with 403 PLAN_LIMIT_EXCEEDED (entitlement
`road_network_costs`). The saved default for travel time has been 'driving' since
2022, so without this every free user's "Populate travel costs" failed the moment
travel time was needed (time windows, or a duration cost metric).

The saved setting is never rewritten. The method is resolved when a request is
built, and the settings screen shows the resolved one, so:
  - a user without road-network access gets straight-line ('geo') for both
    distance and travel time, whatever the saved value says;
  - a Professional user's saved choice, 'driving' included, is sent unchanged;
  - no persisted state changes shape, and an upgrade brings a saved 'driving'
    back without anyone re-choosing it.

Entitlement comes from selectFeatureAllowed(FEATURE_ROAD_NETWORK_COSTS), including
its fail-open rule: while the plan catalog is unknown the saved method is used, so
a paying user is never downgraded because a catalog request failed.
fetchTravelCosts loads the catalog before building a request so that window stays
small.

Import-free on purpose: openapi_conversion_functions and travelCostResults use it,
and both sit in the reducer import graph. Everything is pure except the two
manual-multiplier notice helpers, which read and write one localStorage key.
*/

/** The straight-line method used in place of 'driving' when the road network isn't available. */
export const STRAIGHT_LINE_METHOD = 'geo';

/**
 * The method a request should use for one quantity.
 *
 * @param {string} method - the saved distanceMethod or durationMethod.
 * @param {boolean} [roadNetworkAllowed] - from selectFeatureAllowed(FEATURE_ROAD_NETWORK_COSTS);
 *   undefined means unknown, and the saved method is kept.
 * @returns {string}
 */
export function resolveCostMethod(method, roadNetworkAllowed) {
  if (method === 'driving' && roadNetworkAllowed === false) return STRAIGHT_LINE_METHOD;
  return method;
}

const DEFAULT_COSTS = require('store/data/general_settings.json').costs;

/*
The travel model: how a straight-line (or Manhattan, or x-y) distance becomes a
travel time. The backend converts with

  duration (minutes) = distance × roadCircuityFactor ÷ averageSpeed × 60

(app/services/openroute_functions.py, _distance_to_duration), using 30 and 1.25
for a value it isn't sent or that isn't positive. Examples publish the values
their matrices were built with in generalSettings.costs (backend #361), e.g.
Denver 14 and 1.3; a scenario made on the site has neither.

So each value is sent only when the scenario has a positive number for it, and
left out otherwise, and the backend's defaults apply exactly as before. Driving
durations come from the road network and ignore both.
*/
export const DEFAULT_AVERAGE_SPEED = 30;
export const DEFAULT_ROAD_CIRCUITY = 1.25;

const positive = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The scenario's own travel model: only the values it has, and only valid ones.
 *
 * @param {object} costs - generalSettings.costs
 * @returns {{ averageSpeed?: number, roadCircuityFactor?: number }}
 */
export function travelModel(costs) {
  const model = {};
  const speed = positive(costs && costs.averageSpeed);
  const circuity = positive(costs && costs.roadCircuityFactor);
  if (speed !== null) model.averageSpeed = speed;
  if (circuity !== null) model.roadCircuityFactor = circuity;
  return model;
}

/**
 * A travel time from a distance, as the backend derives it, or null when the
 * scenario has no travel model of its own (then the caller keeps what it did
 * before). A missing half takes the backend's default, as the backend would.
 *
 * @param {number} distance
 * @param {object} costs - generalSettings.costs
 * @returns {number|null} minutes
 */
export function durationFromDistance(distance, costs) {
  const model = travelModel(costs);
  if (model.averageSpeed === undefined && model.roadCircuityFactor === undefined) return null;
  const speed = model.averageSpeed ?? DEFAULT_AVERAGE_SPEED;
  const circuity = model.roadCircuityFactor ?? DEFAULT_ROAD_CIRCUITY;
  return (distance * circuity) / speed * 60;
}

/*
Multipliers and costs entered by hand.

In manual entry (the cost metric's autoCompute set to false) the cost settings
say "Multipliers are disabled when costs are set to manual entry", and hide the
multiplier controls. The request used to send the saved multipliers anyway, so a
cost calculated for a missing value was scaled by a number the person could no
longer see or change. What the screen says now holds: in manual entry every
request sends a multiplier of 1, and a cost derived on the site uses 1 too. The
saved multipliers stay in state, so switching back to automatic restores them.
*/

/**
 * Whether the scenario's costs are entered by hand, which turns multipliers off.
 * Only distance and duration metrics have multipliers; a custom cost is always typed.
 *
 * @param {object} costs - generalSettings.costs
 * @returns {boolean}
 */
export function multipliersOff(costs) {
  if (!costs || (costs.costMetric !== 'distance' && costs.costMetric !== 'duration')) return false;
  return Boolean(costs.autoCompute) && costs.autoCompute[costs.costMetric] === false;
}

/**
 * Whether turning multipliers off changes anything: a saved base multiplier other
 * than 1, or (with costs by vehicle type) one for a vehicle type still in the table.
 *
 * @param {object} costs - generalSettings.costs
 * @param {Array<object>} [vehicleTypes] - data.vehicle_types
 * @returns {boolean}
 */
export function savedMultipliersDifferFromOne(costs, vehicleTypes) {
  const multipliers = (costs && costs.costMultiplier) || {};
  const differs = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) !== 1;
  if (differs(multipliers.all)) return true;
  if (!costs || !costs.vehicleTypeDependence || !multipliers.vehicleTypes) return false;
  const current = new Set((vehicleTypes || []).map((vt) => vt && vt.UUID));
  return Object.entries(multipliers.vehicleTypes).some(([id, value]) => current.has(id) && differs(value));
}

export const MANUAL_MULTIPLIER_NOTICE =
  "Your cost multipliers aren't applied while costs are entered by hand, so calculated costs use a multiplier of 1. "
  + 'Switch costs back to automatic in Cost settings to use them again.';

const MANUAL_MULTIPLIER_NOTICE_KEY = 'optimiciti.manualMultiplierNoticeShown';
const MAX_REMEMBERED_SCENARIOS = 200;

const readShown = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MANUAL_MULTIPLIER_NOTICE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch (e) {
    return [];
  }
};

/**
 * The one-time notice for a scenario whose saved multipliers stop applying in
 * manual entry, or null: when multipliers are on, when they would all be 1
 * anyway, or when this scenario has already been told in this browser.
 *
 * @param {object} args
 * @param {object} args.costs - generalSettings.costs
 * @param {Array<object>} [args.vehicleTypes] - data.vehicle_types
 * @param {string|null} args.scenarioId - the saved scenario's id; null for an
 *   unsaved one, which is told every time (there is nothing stable to remember)
 * @returns {string|null}
 */
export function manualMultiplierNotice({ costs, vehicleTypes, scenarioId }) {
  if (!multipliersOff(costs) || !savedMultipliersDifferFromOne(costs, vehicleTypes)) return null;
  return scenarioId && readShown().includes(String(scenarioId)) ? null : MANUAL_MULTIPLIER_NOTICE;
}

/** Remember that this scenario has been told. Never for an unsaved one. Silently does nothing without storage. */
export function markManualMultiplierNoticeShown(scenarioId) {
  if (!scenarioId) return;
  try {
    const shown = readShown().filter((id) => id !== String(scenarioId));
    shown.push(String(scenarioId));
    window.localStorage.setItem(MANUAL_MULTIPLIER_NOTICE_KEY, JSON.stringify(shown.slice(-MAX_REMEMBERED_SCENARIOS)));
  } catch (e) {
    /* storage unavailable: the notice may show again */
  }
}

/**
 * The notice for a calculation that used straight-line where the person had
 * chosen road-network, or null when there is nothing to tell them.
 *
 * Shown only for a substitution the person would notice:
 *   - the quantity was actually calculated in this request, and
 *   - its saved method is 'driving' AND 'driving' is not the app's default for
 *     it. A value equal to the default was never chosen: everyone starts with
 *     durationMethod 'driving' (general_settings.json, since 2022), so a free
 *     user who never opened Cost settings didn't ask for road travel times and
 *     isn't told they didn't get them. The settings screen already shows them
 *     straight-line. Road distance is not the default, so a saved 'driving'
 *     distance was picked by someone: a Professional user whose plan changed, or
 *     a scenario saved on a Professional account.
 *   - or the scenario is a published example. Its saved methods are the ones its
 *     matrix was built with (backend #361), so a saved 'driving' travel time there
 *     means road-network times are in the table, even though it equals the default
 *     and example rows carry no method tags. A free user who adds a stop to
 *     Lamoille gets straight-line times beside road-network ones, and is told.
 *
 * @param {object} args
 * @param {object} args.costs - generalSettings.costs as saved
 * @param {boolean} args.needDistance - distance is calculated in this request
 * @param {boolean} args.needDuration - travel time is calculated in this request
 * @param {boolean} [args.roadNetworkAllowed] - false only when entitlement says no
 * @param {boolean} args.signedIn - tailors the reason for signed-out visitors
 * @param {boolean} [args.fromExample] - the scenario is a published example
 * @returns {string|null}
 */
export function straightLineFallbackNotice({ costs, rows, needDistance, needDuration, roadNetworkAllowed, signedIn, fromExample = false }) {
  if (roadNetworkAllowed !== false || !costs) return null;
  // The saved method was a real choice when it differs from the app default, or
  // when the matrix shows this scenario really was calculated on the road network
  // before (someone who had access and lost it). Travel time defaults to
  // 'driving', so without the second test a free user who never had road travel
  // times would be told about something they never had.
  const usedBefore = (field) => Array.isArray(rows)
    && rows.some((row) => row && row.costMethod && row.costMethod[field] === 'driving');
  const chosenDriving = (methodKey, field) => costs[methodKey] === 'driving'
    && (DEFAULT_COSTS[methodKey] !== 'driving' || usedBefore(field) || fromExample);
  const distance = Boolean(needDistance) && chosenDriving('distanceMethod', 'distance');
  const duration = Boolean(needDuration) && chosenDriving('durationMethod', 'duration');
  if (!distance && !duration) return null;

  let what;
  let subject;
  let plural;
  if (distance && duration) {
    what = 'straight-line distances and travel times instead of driving ones';
    subject = 'road-network routing';
    plural = false;
  } else if (distance) {
    what = 'straight-line distances instead of driving distances';
    subject = 'road-network distances';
    plural = true;
  } else {
    what = 'straight-line travel times instead of driving times';
    subject = 'road-network routing';
    plural = false;
  }
  const why = signedIn
    ? `${subject} ${plural ? 'are' : 'is'} part of the Professional plan`
    : `${subject} ${plural ? 'need' : 'needs'} a Professional account`;
  return `We used ${what}, because ${why}.`;
}

const METHOD_LABELS = {
  driving: 'the road network',
  geo: 'straight-line (geographic) measurements',
  euclidean: 'straight-line (x-y) measurements',
  manhattan: 'Manhattan measurements',
};

/**
 * What to tell someone whose cost methods were changed by switching between
 * latitude/longitude and x-y coordinates, or null when nothing changed.
 *
 * The methods are not interchangeable — the road network and geographic
 * distances need latitude and longitude, and x-y distances need a plane — so the
 * switch rewrites them. That is invisible unless we say it.
 *
 * Labels the method a calculation would really use, not the saved one: without
 * road-network access a saved 'driving' is calculated straight-line
 * (resolveCostMethod), and telling someone "travel time now uses the road
 * network" while every calculation says otherwise is the contradiction this
 * argument exists to avoid.
 *
 * @param {{distanceMethod: string, durationMethod: string}} before
 * @param {{distanceMethod: string, durationMethod: string}} after
 * @param {string} coordinateSystem - the system just switched to
 * @param {boolean} [roadNetworkAllowed] - from selectRoadNetworkAccess
 * @returns {string|null}
 */
export function coordinateSystemMethodChangeMessage(before, after, coordinateSystem, roadNetworkAllowed) {
  const label = (method) => {
    const effective = resolveCostMethod(method, roadNetworkAllowed);
    return METHOD_LABELS[effective] || effective;
  };
  const changed = [];
  if (label(before.distanceMethod) !== label(after.distanceMethod)) {
    changed.push(`distance now uses ${label(after.distanceMethod)}`);
  }
  if (label(before.durationMethod) !== label(after.durationMethod)) {
    changed.push(`travel time now uses ${label(after.durationMethod)}`);
  }
  if (changed.length === 0) return null;
  const why = coordinateSystem === 'cartesian'
    ? 'road and geographic measurements need latitude and longitude, which x-y coordinates do not have'
    : 'x-y measurements need a plane, not a globe';
  return `Switching coordinates changed how travel costs are measured: ${changed.join(', and ')}, because ${why}. You can change this in Cost settings.`;
}
